import { VueI18n } from "vue-i18n";

/**
 * Abstract base class for error message containers.
 *
 * This class serves as a foundation for structured error handling throughout the application.
 * It extends the native JavaScript `Error` class, allowing error bags to be thrown and caught
 * like standard exceptions while also providing structured access to error data.
 *
 * Subclasses must implement methods for converting errors to both localized and non-localized
 * string representations, enabling consistent error display across different UI contexts.
 *
 * @abstract
 * @extends Error
 *
 * @example
 * ```typescript
 * // Create a custom error bag
 * class CustomErrorBag extends ErrorMessageBag {
 *   constructor(private errors: string[]) {
 *     super();
 *   }
 *
 *   toLocaleString(i18n?: VueI18n): string {
 *     return this.errors.map(e => i18n?.t(e) ?? e).join('\n');
 *   }
 *
 *   toString(): string {
 *     return this.errors.join('\n');
 *   }
 * }
 * ```
 */
export abstract class ErrorMessageBag extends Error {

	/**
	 * Converts all error messages to a localized string representation.
	 *
	 * Implementations should use the provided i18n instance to translate error keys
	 * into user-friendly messages in the appropriate language. If no i18n instance
	 * is provided, implementations should fall back to non-localized messages.
	 *
	 * @param i18n - Optional Vue I18n instance for translation lookup.
	 *               When provided, error keys are translated to the user's language.
	 *               When omitted, raw/English messages are returned.
	 * @returns A string containing all error messages, typically separated by newlines or commas.
	 *
	 * @example
	 * ```typescript
	 * import { useI18n } from 'vue-i18n';
	 *
	 * const { t } = useI18n();
	 * const localizedMessage = errorBag.toLocaleString(i18n);
	 * ```
	 */
	abstract toLocaleString(i18n?: VueI18n): string;

	/**
	 * Converts all error messages to a non-localized string representation.
	 *
	 * This method returns the raw error messages without any translation.
	 * It's useful for logging, debugging, or when i18n is not available.
	 *
	 * @returns A string containing all error messages in their original form,
	 *          typically separated by newlines or commas.
	 *
	 * @example
	 * ```typescript
	 * console.error('API Error:', errorBag.toString());
	 * ```
	 */
	abstract toString(): string;

}
